import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import { CustomFieldResourceType } from "@repo/app-types/entities/customField";
import type { PlaybookTestCallSetup } from "@repo/app-types/entities/playbookTestCase";
import {
  allPlaybookTestCallDirections,
  playbookTestCallDirection,
} from "@repo/app-types/entities/playbookTestCase";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { SegmentedControl } from "@repo/ui/toggleButton/segmentedControl";
import {
  IconChevronDown,
  IconPhone,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, type FC } from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";

type PlaybookTestCustomFieldValue =
  PlaybookTestCallSetup["contact_custom_fields"][number];

const callDirectionLabels: Record<
  (typeof allPlaybookTestCallDirections)[number],
  string
> = {
  [playbookTestCallDirection.Inbound]: "Inbound",
  [playbookTestCallDirection.Outbound]: "Outbound",
};

const callDirectionOptions = allPlaybookTestCallDirections.map((direction) => ({
  id: direction,
  label: callDirectionLabels[direction],
}));

/**
 * An empty setup, so a case that had none starts from a known shape. Inbound,
 * because most agents only ever receive calls.
 */
export const createEmptyPlaybookTestCallSetup = (): PlaybookTestCallSetup => ({
  direction: playbookTestCallDirection.Inbound,
  contact: { name: null, email: null, phone_number: null },
  contact_custom_fields: [],
  interaction_custom_fields: [],
});

/** A row the person has not finished picking a field for. */
const checkCustomFieldRowIncomplete = (
  value: PlaybookTestCustomFieldValue,
): boolean => value.custom_field_definition_id === "";

/**
 * The setup as it leaves the editor: a row still waiting for a field is nothing
 * yet, not a value the setup has to carry. The editor keeps its own empty rows
 * so they stay editable.
 */
export const dropIncompletePlaybookTestCallSetupFields = (
  callSetup: PlaybookTestCallSetup | null,
): PlaybookTestCallSetup | null => {
  if (!callSetup) {
    return null;
  }

  return {
    ...callSetup,
    contact_custom_fields: callSetup.contact_custom_fields.filter(
      (value) => !checkCustomFieldRowIncomplete(value),
    ),
    interaction_custom_fields: callSetup.interaction_custom_fields.filter(
      (value) => !checkCustomFieldRowIncomplete(value),
    ),
  };
};

/** Empty means "the agent is told nothing", which the setup stores as null. */
const toNullableText = (value: string): string | null =>
  value.trim() === "" ? null : value;

/**
 * What the setup carries, in one line, so a collapsed section says what it
 * holds: "Outbound, Susan Reed, 3 fields".
 */
export const describePlaybookTestCallSetup = (
  callSetup: PlaybookTestCallSetup | null,
): string => {
  if (!callSetup) {
    return callDirectionLabels[playbookTestCallDirection.Inbound];
  }

  const parts = [callDirectionLabels[callSetup.direction]];

  // An inbound call only ever carries caller ID: a retained name, email or
  // custom field is kept for switching back to outbound, not something the
  // agent is actually told, so it stays out of the summary too.
  if (callSetup.direction === playbookTestCallDirection.Inbound) {
    if (callSetup.contact.phone_number !== null) {
      parts.push(callSetup.contact.phone_number);
    }
    return parts.join(", ");
  }

  const contact =
    callSetup.contact.name ??
    callSetup.contact.phone_number ??
    callSetup.contact.email;
  if (contact !== null) {
    parts.push(contact);
  }

  const fieldCount =
    callSetup.contact_custom_fields.length +
    callSetup.interaction_custom_fields.length;
  if (fieldCount > 0) {
    parts.push(fieldCount === 1 ? "1 field" : `${fieldCount} fields`);
  }

  return parts.join(", ");
};

interface CustomFieldValueListProps {
  label: string;
  description: string;
  addButtonLabel: string;
  definitions: readonly CustomFieldDefinition[];
  emptyDefinitionsMessage: string;
  values: readonly PlaybookTestCustomFieldValue[];
  onChange: (values: PlaybookTestCustomFieldValue[]) => void;
}

/** One resource type's fields: a definition and a value per row. */
const CustomFieldValueList: FC<CustomFieldValueListProps> = (props) => {
  const {
    label,
    description,
    addButtonLabel,
    definitions,
    emptyDefinitionsMessage,
    values,
    onChange,
  } = props;

  const options: SimpleSelectOption<string>[] = definitions.map(
    (definition) => ({
      value: definition.id,
      label: definition.display_name,
    }),
  );

  const updateValue = (
    index: number,
    changes: Partial<PlaybookTestCustomFieldValue>,
  ) => {
    onChange(
      values.map((value, valueIndex) =>
        valueIndex === index ? { ...value, ...changes } : value,
      ),
    );
  };

  return (
    <div className="flex flex-col space-y-2">
      <Label size={Size.SMALL}>{label}</Label>
      <FieldDescription>
        {definitions.length === 0 ? emptyDefinitionsMessage : description}
      </FieldDescription>
      {values.map((value, index) => (
        <div key={index} className="flex items-start gap-2">
          <div className="w-1/2">
            <SimpleSelect<string>
              label="Custom field"
              isLabelHidden
              isFullWidth
              placeholder="Pick a field"
              options={options}
              // A field already picked in another row would overwrite it.
              disabledValues={values
                .filter((_, otherIndex) => otherIndex !== index)
                .map((other) => other.custom_field_definition_id)}
              value={value.custom_field_definition_id || null}
              onChange={(customFieldDefinitionId) =>
                updateValue(index, {
                  custom_field_definition_id: customFieldDefinitionId,
                })
              }
            />
          </div>
          <div className="flex-1">
            <Input
              accessibleLabel="Value"
              placeholder="Value"
              value={value.value}
              onChange={(event) =>
                updateValue(index, { value: event.target.value })
              }
            />
          </div>
          <IconButton
            icon={<IconTrash className="h-4 w-4" />}
            variant={ButtonVariant.Tertiary}
            accessibleLabel="Remove field"
            onClick={() =>
              onChange(values.filter((_, valueIndex) => valueIndex !== index))
            }
          />
        </div>
      ))}
      {/* Every definition already has a row, so another one has nothing to pick. */}
      {values.length >= definitions.length ? null : (
        <div>
          <Button
            variant={ButtonVariant.Secondary}
            size={Size.MEDIUM}
            icon={<IconPlus className="h-4 w-4" />}
            onClick={() =>
              onChange([
                ...values,
                { custom_field_definition_id: "", value: "" },
              ])
            }
          >
            {addButtonLabel}
          </Button>
        </div>
      )}
    </div>
  );
};

export interface PlaybookTestCallSetupEditorProps {
  value: PlaybookTestCallSetup | null;
  onChange: (value: PlaybookTestCallSetup | null) => void;
  /**
   * Whether this agent can place outbound calls at all. When it cannot, the
   * direction toggle is hidden rather than offering a call that can never
   * happen — but a stored outbound direction is left exactly as it is.
   */
  isOutboundAvailable: boolean;
}

/**
 * What the agent is told before a simulated call: who it thinks it is calling
 * and what it already knows about them. Shared by the test case editor and
 * "Try this agent", which set the same thing.
 */
export const PlaybookTestCallSetupEditor: FC<
  PlaybookTestCallSetupEditorProps
> = (props) => {
  const { value, onChange, isOutboundAvailable } = props;

  const { data: customFieldDefinitions } =
    useAllUserCustomFieldDefinitionsQuery();

  const contactDefinitions = useMemo(
    () =>
      customFieldDefinitions?.filter(
        (definition) =>
          definition.resource_type === CustomFieldResourceType.CONTACT,
      ) ?? [],
    [customFieldDefinitions],
  );
  const interactionDefinitions = useMemo(
    () =>
      customFieldDefinitions?.filter(
        (definition) =>
          definition.resource_type === CustomFieldResourceType.GRADING,
      ) ?? [],
    [customFieldDefinitions],
  );

  // Editing an unset setup fills one in rather than binding to nothing.
  const callSetup = value ?? createEmptyPlaybookTestCallSetup();
  // An inbound call only ever carries caller ID; the rest is kept, just
  // hidden, so switching back to outbound doesn't lose what was typed.
  const showOutboundOnlyFields =
    callSetup.direction === playbookTestCallDirection.Outbound;

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2">
        {isOutboundAvailable ? (
          <>
            <SegmentedControl
              aria-label="Call direction"
              size={Size.SMALL}
              options={callDirectionOptions}
              value={callSetup.direction}
              onChange={(direction) => onChange({ ...callSetup, direction })}
            />
            <FieldDescription>
              On an inbound call the agent is told the phone number and nothing
              else, the way a real inbound call reaches it. The rest is kept for
              when you switch back to outbound.
            </FieldDescription>
          </>
        ) : (
          <FieldDescription>
            {callSetup.direction === playbookTestCallDirection.Outbound
              ? "This agent only takes inbound calls. This case is still set to outbound."
              : "This agent only takes inbound calls, so the agent is told the phone number and nothing else, the way a real inbound call reaches it."}
          </FieldDescription>
        )}
      </div>
      {showOutboundOnlyFields ? (
        <>
          <Input
            label="Contact name"
            value={callSetup.contact.name ?? ""}
            onChange={(event) =>
              onChange({
                ...callSetup,
                contact: {
                  ...callSetup.contact,
                  name: toNullableText(event.target.value),
                },
              })
            }
            placeholder="Susan Reed"
          />
          <Input
            label="Contact email"
            value={callSetup.contact.email ?? ""}
            onChange={(event) =>
              onChange({
                ...callSetup,
                contact: {
                  ...callSetup.contact,
                  email: toNullableText(event.target.value),
                },
              })
            }
            placeholder="susan.reed@example.com"
          />
        </>
      ) : null}
      <Input
        label="Contact phone number"
        value={callSetup.contact.phone_number ?? ""}
        onChange={(event) =>
          onChange({
            ...callSetup,
            contact: {
              ...callSetup.contact,
              phone_number: toNullableText(event.target.value),
            },
          })
        }
        placeholder="+15551234567"
      />
      {showOutboundOnlyFields ? (
        <>
          <CustomFieldValueList
            label="Contact fields"
            description="What the agent already knows about the contact."
            addButtonLabel="Add contact field"
            definitions={contactDefinitions}
            emptyDefinitionsMessage="No contact custom fields are configured."
            values={callSetup.contact_custom_fields}
            onChange={(contactCustomFields) =>
              onChange({
                ...callSetup,
                contact_custom_fields: contactCustomFields,
              })
            }
          />
          <CustomFieldValueList
            label="Customer interaction fields"
            description="What the agent already knows about this interaction."
            addButtonLabel="Add customer interaction field"
            definitions={interactionDefinitions}
            emptyDefinitionsMessage="No customer interaction custom fields are configured."
            values={callSetup.interaction_custom_fields}
            onChange={(interactionCustomFields) =>
              onChange({
                ...callSetup,
                interaction_custom_fields: interactionCustomFields,
              })
            }
          />
        </>
      ) : null}
      {value === null ? null : (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => onChange(null)}
        >
          Clear call setup
        </Button>
      )}
    </div>
  );
};

export interface PlaybookTestCallSetupSectionProps {
  value: PlaybookTestCallSetup | null;
  onChange: (value: PlaybookTestCallSetup | null) => void;
  /** Whether this agent can place outbound calls at all. */
  isOutboundAvailable: boolean;
}

/**
 * The editor behind a collapsed disclosure that says what the setup holds, so
 * a surface can show what the agent will be told without opening anything.
 */
export const PlaybookTestCallSetupSection: FC<
  PlaybookTestCallSetupSectionProps
> = (props) => {
  const { value, onChange, isOutboundAvailable } = props;

  return (
    <Disclosure className="rounded-lg border border-border-light bg-white">
      <Heading>
        <AriaButton
          slot="trigger"
          className="group w-full min-w-0 cursor-pointer text-left"
        >
          <div className="flex items-center gap-3 px-3 py-2.5">
            <IconPhone className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 text-sm font-medium text-foreground">
              Call setup
            </div>
            <div className="min-w-0 truncate text-sm text-muted-foreground">
              {describePlaybookTestCallSetup(value)}
            </div>
            <IconChevronDown className="size-4 shrink-0 text-gray-400 transition-transform group-aria-expanded:rotate-180" />
          </div>
        </AriaButton>
      </Heading>
      <DisclosurePanel>
        <div className="border-t border-border-light px-3 py-3">
          <PlaybookTestCallSetupEditor
            value={value}
            onChange={onChange}
            isOutboundAvailable={isOutboundAvailable}
          />
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
};
